import React, { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Sparkles, Trash2 } from "lucide-react";

interface Novidade {
  id: string;
  titulo: string;
  descricao: string;
  link: string | null;
  metodo_slug: string | null;
  card_path: string | null;
  ativo: boolean;
  publicado_em: string;
}

const AdminNovidades: React.FC = () => {
  const [items, setItems] = useState<Novidade[]>([]);
  const [titulo, setTitulo] = useState("");
  const [descricao, setDescricao] = useState("");
  const [link, setLink] = useState("");
  const [cardPath, setCardPath] = useState("");
  const [metodoSlug, setMetodoSlug] = useState("");

  const carregar = async () => {
    const { data } = await supabase
      .from("novidades")
      .select("*")
      .order("publicado_em", { ascending: false });
    setItems((data || []) as Novidade[]);
  };

  useEffect(() => {
    carregar();
  }, []);

  const criar = async () => {
    if (!titulo || !descricao) {
      toast.error("Título e descrição são obrigatórios");
      return;
    }
    const { error } = await supabase.from("novidades").insert({
      titulo,
      descricao,
      link: link || null,
      card_path: cardPath || null,
      metodo_slug: metodoSlug || null,
    });
    if (error) {
      toast.error("Erro ao criar novidade");
      return;
    }
    toast.success("Novidade publicada!");
    setTitulo("");
    setDescricao("");
    setLink("");
    setCardPath("");
    setMetodoSlug("");
    carregar();
  };

  const toggleAtivo = async (n: Novidade) => {
    await supabase.from("novidades").update({ ativo: !n.ativo }).eq("id", n.id);
    carregar();
  };

  const excluir = async (id: string) => {
    await supabase.from("novidades").delete().eq("id", id);
    toast.success("Novidade excluída");
    carregar();
  };

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Sparkles className="w-5 h-5 text-amber-500" />
            Publicar Novidade
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div>
            <Label>Título</Label>
            <Input value={titulo} onChange={(e) => setTitulo(e.target.value)} />
          </div>
          <div>
            <Label>Descrição</Label>
            <Textarea value={descricao} onChange={(e) => setDescricao(e.target.value)} />
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div>
              <Label>Link (opcional)</Label>
              <Input value={link} onChange={(e) => setLink(e.target.value)} placeholder="/metodos?method=..." />
            </div>
            <div>
              <Label>Card path (Dashboard)</Label>
              <Input value={cardPath} onChange={(e) => setCardPath(e.target.value)} placeholder="/metodos" />
            </div>
            <div>
              <Label>Método slug</Label>
              <Input value={metodoSlug} onChange={(e) => setMetodoSlug(e.target.value)} placeholder="centena-matadora" />
            </div>
          </div>
          <Button onClick={criar} className="w-full">Publicar</Button>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>Novidades publicadas ({items.length})</CardTitle>
        </CardHeader>
        <CardContent className="space-y-2">
          {items.map((n) => (
            <div key={n.id} className="flex items-center justify-between p-3 border rounded-lg gap-3">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <span className="font-semibold truncate">{n.titulo}</span>
                  {!n.ativo && <Badge variant="secondary">Inativa</Badge>}
                </div>
                <p className="text-sm text-muted-foreground truncate">{n.descricao}</p>
              </div>
              <Switch checked={n.ativo} onCheckedChange={() => toggleAtivo(n)} />
              <Button size="sm" variant="ghost" onClick={() => excluir(n.id)}>
                <Trash2 className="w-4 h-4" />
              </Button>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

export default AdminNovidades;
